import { createContext, useContext, useEffect, useState } from "react";

const KEY = "ra_market";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const MARKETS = {
  global: { id: "global", label: "Global", flag: "🌏", currency: "$", currencyCode: "USD" },
  uk: { id: "uk", label: "United Kingdom", flag: "🇬🇧", currency: "£", currencyCode: "GBP" },
  eu: { id: "eu", label: "Europe", flag: "🇪🇺", currency: "€", currencyCode: "EUR" },
  au: { id: "au", label: "Australia", flag: "🇦🇺", currency: "A$", currencyCode: "AUD" },
};

// USD is the source of truth. Non-USD markets display a fixed managed rate (1 USD → local),
// editable by the owner in the Owner Console → Currency. These are only fallback defaults.
export const FX_RATES = { global: 1, uk: 0.79, eu: 0.92, au: 1.50 };
const DEFAULT_ROUND = 50;
export const USD_TO_AUD = FX_RATES.au; // backward-compat

export function fmtMarketPrice(usd, market, rates = FX_RATES, round = DEFAULT_ROUND) {
  if (usd == null || usd === "") return "";
  const n = Number(usd);
  const cfg = MARKETS[market] || MARKETS.global;
  if (market === "global") return `$${n.toLocaleString(undefined, Number.isInteger(n) ? {} : { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const rate = rates[market] ?? FX_RATES[market] ?? 1;
  const step = round || DEFAULT_ROUND;
  const converted = Math.round((n * rate) / step) * step;
  return `${cfg.currency}${converted.toLocaleString()}`;
}

const MarketContext = createContext({ market: "global", setMarket: () => {}, cfg: MARKETS.global });

export function MarketProvider({ children }) {
  const [market, setMarketState] = useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(KEY) : null;
    return saved && MARKETS[saved] ? saved : "global";
  });
  const [rates, setRates] = useState(FX_RATES);
  const [round, setRound] = useState(DEFAULT_ROUND);

  const setMarket = (m) => {
    if (!MARKETS[m]) return;
    localStorage.setItem(KEY, m);
    setMarketState(m);
  };

  useEffect(() => {
    localStorage.setItem(KEY, market);
  }, [market]);

  // load owner-managed FX rates from the backend (falls back to defaults if unavailable)
  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/fx-rates`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        if (d.rates) setRates({ ...FX_RATES, ...d.rates });
        if (d.round) setRound(d.round);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const price = (usd) => fmtMarketPrice(usd, market, rates, round);
  const isUSD = market === "global";
  const priceNote = isUSD ? "" : "Indicative — billed in USD at checkout";

  return (
    <MarketContext.Provider value={{ market, setMarket, cfg: MARKETS[market], rates, round, price, priceNote, isUSD }}>
      {children}
    </MarketContext.Provider>
  );
}

export const useMarket = () => useContext(MarketContext);
